/**
 * Keeps both search tab counts populated, and canonicalises unscoped /search URLs.
 *
 * Liquid renders the active tab's count; the inactive one is fetched from
 * templates/search.count.liquid and cached so later renders paint it synchronously.
 */
const CACHE_PREFIX = 'search-tab-count:';
const FILTER_PREFIX = 'search-tab-filters:';

/** The current URL's facet and sort params. Read from location, which pushState keeps current. */
function carriedFilters() {
  const params = new URLSearchParams();
  for (const [key, value] of new URL(window.location.href).searchParams) {
    if (key.startsWith('filter.') || key === 'sort_by') params.append(key, value);
  }
  return params.toString();
}

/** @param {string} key Fully-qualified storage key. @returns {string | null} */
function readStore(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    // Private mode or storage disabled; fetch-only is a fine fallback.
    return null;
  }
}

/** @param {string} key Fully-qualified storage key. @param {string} value */
function writeStore(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* not fatal */
  }
}

/** @param {string} key @returns {string | null} */
const readCache = (key) => readStore(CACHE_PREFIX + key);
/** @param {string} key @param {string} value */
const writeCache = (key, value) => writeStore(CACHE_PREFIX + key, value);

class SearchTabs extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;
  #writing = false;
  /** @type {string | null} Endpoint already requested, so the observer cannot refetch per mutation. */
  #fetched = null;

  connectedCallback() {
    // An unscoped search covers all three object types, so results_count would not match the
    // "Product Results" label. The server decides; a size test would misfire on type=article,page.
    if (this.hasAttribute('data-search-unscoped')) {
      const url = new URL(window.location.href);
      url.searchParams.set('type', 'product');
      window.location.replace(url.toString());
      return;
    }

    this.#syncFilters();
    this.#publishOwnCount();
    this.#hydrate();

    // Facets apply without a page load, so this is the only chance to record them before navigating.
    this.addEventListener('click', this.#onClick, { capture: true });

    // A facet update may morph this element in place, skipping connectedCallback and leaving the
    // emptied span unfilled. Watching for that is what makes the count survive filtering.
    this.#observer = new MutationObserver(() => {
      if (this.#writing) return;
      this.#syncFilters();
      this.#publishOwnCount();
      this.#hydrate();
    });
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick, { capture: true });
  }

  #onClick = () => {
    this.#syncFilters();
    // Also cache the latest figure, in case a re-render landed too late for the observer.
    this.#publishOwnCount();
  };

  /**
   * Keeps the product tab's filter memory in step: record the facets on the products tab, re-apply
   * them to that tab's link and count on the recipes tab.
   */
  #syncFilters() {
    const link = this.querySelector('[data-search-tab-link="product"][data-search-tab-base]');
    if (!link) return;

    const base = link.getAttribute('data-search-tab-base');
    if (!base) return;

    if (this.dataset.activeTab === 'product') {
      const carried = carriedFilters();
      // Unconditional, so clearing the facets clears the memory rather than leaving a stale set.
      writeStore(FILTER_PREFIX + base, carried);

      // Re-key this tab's count to the filtered URL, or the filtered figure is never cached and the
      // recipes tab blanks until its fetch lands.
      const self = this.querySelector('[data-search-tab-self]');
      const href = carried ? `${base}&${carried}` : base;
      if (self && self.getAttribute('data-search-tab-key') !== href) {
        self.setAttribute('data-search-tab-key', href);
      }
      return;
    }

    const carried = readStore(FILTER_PREFIX + base);
    const href = carried ? `${base}&${carried}` : base;
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);

    const span = link.querySelector('[data-search-tab-count]');
    if (!span) return;
    if (span.getAttribute('data-search-tab-key') !== href) {
      span.setAttribute('data-search-tab-key', href);
    }
    const endpoint = `${href}&view=count`;
    if (span.getAttribute('data-search-tab-count') !== endpoint) {
      span.setAttribute('data-search-tab-count', endpoint);
    }
  }

  /**
   * Caches the active tab's server-rendered count under the URL that tab points at. The guard
   * covers the theme editor, where the inline script is skipped and the key is still unfiltered.
   */
  #publishOwnCount() {
    const self = this.querySelector('[data-search-tab-self][data-search-tab-key]');
    if (!self) return;

    const key = self.getAttribute('data-search-tab-key');
    if (!key) return;

    const urlFiltered = [...new URL(window.location.href).searchParams.keys()].some((k) =>
      k.startsWith('filter.')
    );
    if (urlFiltered && !key.includes('filter.')) return;

    const count = self.getAttribute('data-search-tab-self');
    if (/^\d+$/.test(count ?? '')) writeCache(key, /** @type {string} */ (count));
  }

  /**
   * Fills the inactive tab from cache, and only goes to the network when nothing is cached for
   * this exact query and filter set — every fetch is a /search hit carrying the shopper's query.
   */
  #hydrate() {
    const target = this.querySelector('[data-search-tab-count]');
    if (!target) return;

    const key = target.getAttribute('data-search-tab-key');

    // Keyed on query + type + facets, so a new search or a changed filter still fetches. A count
    // cannot change mid-session, and sessionStorage is dropped when the tab closes.
    const cached = key ? readCache(key) : null;
    if (cached) {
      if (target.textContent?.trim() !== `(${cached})`) this.#write(target, cached);
      return;
    }

    // Deduplicated by endpoint so the observer cannot spawn a fetch per mutation.
    const endpoint = target.getAttribute('data-search-tab-count');
    if (endpoint && endpoint !== this.#fetched) {
      this.#fetched = endpoint;
      this.#fetchCount(target, key);
    }
  }

  /**
   * @param {Element} target
   * @param {string | null} key
   */
  async #fetchCount(target, key) {
    const endpoint = target.getAttribute('data-search-tab-count');
    if (!endpoint) return;

    let body;
    try {
      const response = await fetch(endpoint);
      if (!response.ok) return;
      body = (await response.text()).trim();
    } catch {
      // Offline or aborted; whatever the cache painted stands.
      return;
    }

    // .shopifyignore excludes templates/*, so if search.count.liquid is missing this returns the
    // whole search page. Only ever write a bare integer.
    if (!/^\d+$/.test(body)) return;

    if (key) writeCache(key, body);
    this.#write(target, body);
  }

  /**
   * Guarded so our own DOM write does not re-trigger the observer.
   *
   * @param {Element} target
   * @param {string} count
   */
  #write(target, count) {
    this.#writing = true;
    target.textContent = `(${count})`;
    // Release next microtask, after the observer callback for this mutation has run.
    Promise.resolve().then(() => {
      this.#writing = false;
    });
  }
}

customElements.define('search-tabs', SearchTabs);
